import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, UploadSimple, FilePdf, Trash, FileArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUS_OPTS = [
  { v: "not_started", label: "Not Started", cls: "bg-stone-100 text-stone-600 border-stone-300" },
  { v: "sent", label: "Sent / Given", cls: "bg-[#D68910]/10 text-[#9a6a0b] border-[#D68910]/40" },
  { v: "received", label: "Received (Signed)", cls: "bg-[#27AE60]/10 text-[#1e8449] border-[#27AE60]/30" },
];

const emptyMeds = () => ({
  date: new Date().toISOString().slice(0, 10),
  entries: Array.from({ length: 10 }, () => ({ name: "", dosage: "", frequency: "" })),
  notes: "",
});

export default function PaperworkPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [paperwork, setPaperwork] = useState(null);
  const [meds, setMeds] = useState(null);
  const [savingMeds, setSavingMeds] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileInputs = useRef({});

  const load = async () => {
    try {
      const [c, p] = await Promise.all([api.get(`/clients/${id}`), api.get(`/clients/${id}/paperwork`)]);
      setClient(c.data);
      setPaperwork(p.data);
      const saved = c.data.forms?.medications;
      if (saved && (saved.entries?.length || saved.notes)) {
        const entries = [...(saved.entries || [])];
        while (entries.length < 10) entries.push({ name: "", dosage: "", frequency: "" });
        setMeds({ date: saved.date_reviewed || saved.date || "", entries, notes: saved.notes || "" });
      } else {
        setMeds(emptyMeds());
      }
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!client || !paperwork || !meds) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  const setEntry = (i, k, v) => {
    const entries = meds.entries.map((e, idx) => (idx === i ? { ...e, [k]: v } : e));
    setMeds({ ...meds, entries });
  };

  const saveMeds = async () => {
    setSavingMeds(true);
    try {
      await api.put(`/clients/${id}/forms/medications`, { date_reviewed: meds.date, entries: meds.entries, notes: meds.notes, source: meds.source || "", source_other: meds.source_other || "" });
      toast.success("Medication list saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingMeds(false);
    }
  };

  const setStatus = async (key, status) => {
    try {
      await api.put(`/clients/${id}/paperwork/${key}/status`, { status });
      setPaperwork((p) => p.map((d) => (d.key === key ? { ...d, status } : d)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const upload = async (key, file) => {
    if (!file) return;
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/clients/${id}/paperwork/${key}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Signed copy uploaded");
      const p = await api.get(`/clients/${id}/paperwork`);
      setPaperwork(p.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(null);
      if (fileInputs.current[key]) fileInputs.current[key].value = "";
    }
  };

  const removeFile = async (key) => {
    try {
      await api.delete(`/clients/${id}/paperwork/${key}/file`);
      setPaperwork((p) => p.map((d) => (d.key === key ? { ...d, file: null } : d)));
      toast.success("Uploaded file removed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const clientName = client.forms?.intake?.full_name || "";

  return (
    <div data-testid="paperwork-page">
      <button onClick={() => navigate(`/clients/${id}`)} className="mb-4 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="back-to-client">
        <ArrowLeft size={14} /> Back to assessments
      </button>
      <div className="mb-8 flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Paperwork</h1>
        <span className="rounded-lg border border-stone-200 bg-accent px-3 py-1 text-sm font-semibold text-accent-foreground">{client.forms?.intake?.full_name || client.client_code}</span>
        <span className="text-sm text-stone-500">{clientName}</span>
      </div>

      {/* From the intake — filled, editable online, ready to print */}
      {(() => {
        const hasCareplan = !!client.forms?.careplan;
        const careplanSigned = !!(client.forms?.approval?.client_signature_png || client.forms?.approval?.client_signature_date);
        const agr = client.agreement || null;
        const agrSigned = !!(agr?.signed?.signed_at);
        const acks = paperwork.filter((d) => d.group === "Acknowledgements");
        const acksSigned = acks.filter((d) => d.signed).length;
        const chip = (on, label, offLabel) => (
          <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${on ? "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]" : "border-[#D68910]/40 bg-[#D68910]/10 text-[#9a6a0b]"}`}>
            {on ? label : offLabel}
          </span>
        );
        const btnPrimary = "inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground transition-colors hover:bg-stone-900";
        const btnOutline = "inline-flex h-8 items-center gap-1.5 rounded-md border border-stone-300 px-3 text-xs font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white";
        return (
          <div className="mb-8" data-testid="ready-from-intake">
            <h2 className="font-heading text-lg font-semibold text-stone-900">From the intake — filled in &amp; ready</h2>
            <p className="mb-4 mt-0.5 text-xs text-stone-500">These are already filled from this client's intake answers. Review and edit online first, then print or send for signature.</p>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="ready-careplan">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-heading text-sm font-semibold text-stone-900">Care Plan</p>
                  {careplanSigned ? chip(true, "Signed") : hasCareplan ? chip(false, "", "Ready — unsigned") : <span className="rounded-full border border-stone-200 bg-stone-100 px-2.5 py-1 text-xs text-stone-500">Not generated yet</span>}
                </div>
                <p className="mt-1 text-xs leading-relaxed text-stone-500">Tasks, levels of care, schedule, precautions — generated from the intake, yours to edit before it prints.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link to={`/clients/${id}/careplan-review`} className={btnPrimary} data-testid="ready-careplan-edit">Review &amp; Edit</Link>
                  <a href={`${api.defaults.baseURL}/clients/${id}/careplan/print.pdf`} target="_blank" rel="noreferrer" className={btnOutline} data-testid="ready-careplan-pdf">Print / Download</a>
                </div>
              </div>
              <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="ready-agreement">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-heading text-sm font-semibold text-stone-900">Service Agreement</p>
                  {agrSigned ? chip(true, "Signed") : agr ? chip(false, "", "Ready — unsigned") : <span className="rounded-full border border-stone-200 bg-stone-100 px-2.5 py-1 text-xs text-stone-500">Not generated yet</span>}
                </div>
                <p className="mt-1 text-xs leading-relaxed text-stone-500">Terms, schedule, and the authorization sections — prefilled from the intake. Edit online; initials and signature collect online or on paper.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link to={`/clients/${id}/service-agreement`} className={btnPrimary} data-testid="ready-agreement-edit">Review &amp; Edit</Link>
                  <a href={`${api.defaults.baseURL}/clients/${id}/agreement/pdf`} target="_blank" rel="noreferrer" className={btnOutline} data-testid="ready-agreement-pdf">Print / Download</a>
                </div>
              </div>
            </div>
            <div className="mt-4 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="ready-acknowledgements">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-heading text-sm font-semibold text-stone-900">Acknowledgements</p>
                  <p className="mt-0.5 text-xs text-stone-500">{acksSigned} of {acks.length} signed — each signs individually, online or on paper.</p>
                </div>
                <div className="flex items-center gap-2">
                  {chip(acksSigned === acks.length && acks.length > 0, "All signed", `${acksSigned}/${acks.length} signed`)}
                  <a href={`${api.defaults.baseURL}/clients/${id}/signing-packet.pdf`} target="_blank" rel="noreferrer" className={btnOutline} data-testid="ready-packet-pdf">Print whole packet</a>
                  <a href={`${api.defaults.baseURL}/clients/${id}/signing-packet.pdf?only=signatures`} target="_blank" rel="noreferrer" className={btnOutline} data-testid="ready-signature-pages-pdf">Print signature pages</a>
                  <a href={`${api.defaults.baseURL}/clients/${id}/packet-cover.pdf`} target="_blank" rel="noreferrer" className={btnOutline} data-testid="ready-packet-cover-pdf">Print cover sheet</a>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {acks.map((d) => (
                  <div key={d.key} className="flex items-center justify-between gap-3 rounded-lg border border-stone-100 bg-stone-50/60 px-3 py-2" data-testid={`ready-ack-${d.key}`}>
                    <p className="min-w-0 flex-1 truncate text-xs font-medium text-stone-700">{d.title}</p>
                    {d.signed
                      ? <span className="shrink-0 text-xs font-semibold text-[#1e8449]">Signed {String(d.signed.signed_at).slice(0, 10)}</span>
                      : d.key === "preferred_caregiver_agreement"
                        ? <a href={`${api.defaults.baseURL}/clients/${id}/preferred-caregiver-agreement.pdf`} target="_blank" rel="noreferrer" className="shrink-0 text-xs font-semibold text-primary hover:underline" data-testid={`ready-ack-fill-${d.key}`}>Print →</a>
                        : <Link to={`/clients/${id}/paperwork/${d.key}`} className="shrink-0 text-xs font-semibold text-primary hover:underline" data-testid={`ready-ack-fill-${d.key}`}>Fill &amp; Sign →</Link>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div>
          <h2 className="font-heading text-lg font-semibold text-stone-900">Intake Packet Tracking</h2>
          <p className="mb-4 mt-0.5 text-xs text-stone-500">Blank templates are on the <a href="/templates" className="font-medium text-primary hover:underline">Templates page</a>. Upload signed copies here.</p>
          <div className="space-y-3" data-testid="paperwork-list">
            {["Intake Packet", "Acknowledgements"].flatMap((grp) => [
              <h3 key={`h-${grp}`} className="pt-4 font-heading text-xs font-semibold uppercase tracking-wider text-stone-400 first:pt-0">{grp}</h3>,
              ...paperwork.filter((d) => d.group === grp).map((d) => {
              const s = STATUS_OPTS.find((o) => o.v === d.status) || STATUS_OPTS[0];
              return (
                <div key={d.key} className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid={`paperwork-${d.key}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-heading text-sm font-semibold text-stone-900">{d.title}</p>
                      <p className="mt-0.5 text-xs text-stone-500">{d.desc}</p>
                    </div>
                    <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${s.cls}`} data-testid={`paperwork-status-${d.key}`}>{s.label}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Select value={d.status} onValueChange={(v) => setStatus(d.key, v)}>
                      <SelectTrigger className="h-8 w-44 border-stone-300 bg-white text-xs" data-testid={`paperwork-status-select-${d.key}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTS.map((o) => <SelectItem key={o.v} value={o.v} data-testid={`paperwork-status-option-${d.key}-${o.v}`}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Link to={`/clients/${id}/paperwork/${d.key}`} data-testid={`paperwork-fill-sign-${d.key}`}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground transition-colors hover:bg-stone-900">
                      Fill & Sign
                    </Link>
                    <input type="file" accept=".pdf,image/*" hidden ref={(el) => (fileInputs.current[d.key] = el)}
                      onChange={(e) => upload(d.key, e.target.files?.[0])} data-testid={`paperwork-file-input-${d.key}`} />
                    <Button variant="outline" size="sm" disabled={uploading === d.key}
                      onClick={() => fileInputs.current[d.key]?.click()}
                      className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid={`paperwork-upload-${d.key}`}>
                      <UploadSimple size={14} className="mr-1.5" /> {uploading === d.key ? "Uploading..." : d.file ? "Replace Signed Copy" : "Upload Signed Copy"}
                    </Button>
                  </div>
                  {d.signed && (
                    <p className="mt-2 text-xs font-medium text-[#1e8449]" data-testid={`paperwork-signed-${d.key}`}>
                      Signed electronically by {d.signed.signer_name} on {String(d.signed.signed_at).slice(0, 10)}
                    </p>
                  )}
                  {d.file && (
                    <div className="mt-3 flex items-center gap-2 rounded-lg border border-stone-200 bg-accent/60 px-3 py-2 text-xs text-stone-700" data-testid={`paperwork-file-${d.key}`}>
                      <FilePdf size={16} className="shrink-0 text-[#C0392B]" />
                      <span className="min-w-0 flex-1 truncate">{d.file.filename}</span>
                      <span className="shrink-0 text-stone-500">{Math.round((d.file.size || 0) / 1024)} KB</span>
                      <a href={`${api.defaults.baseURL}/clients/${id}/paperwork/${d.key}/${d.generated ? "generated" : "download"}`} target="_blank" rel="noreferrer"
                        className="shrink-0 font-medium text-primary hover:text-stone-900" data-testid={`paperwork-download-${d.key}`}>
                        <FileArrowDown size={16} />
                      </a>
                      <button onClick={() => removeFile(d.key)} className="shrink-0 text-stone-400 hover:text-[#C0392B]" data-testid={`paperwork-delete-${d.key}`}>
                        <Trash size={15} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })])}
          </div>
        </div>

        <div>
          <h2 className="font-heading text-lg font-semibold text-stone-900">Medication List (Digital)</h2>
          <p className="mb-4 mt-0.5 text-xs text-stone-500">Digital version of the packet medication list. Included in AI care plan generation when present.</p>
          <div className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="medication-form">
            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-stone-500">Client</p>
                <p className="mt-1 text-sm font-semibold text-stone-900" data-testid="med-client-name">{clientName || client.client_code}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-stone-500">Date</p>
                <Input type="date" value={meds.date} onChange={(e) => setMeds({ ...meds, date: e.target.value })}
                  className="mt-1 h-9 border-stone-300 bg-white" data-testid="med-date" />
              </div>
            </div>
            <div className="space-y-2.5">
              <div className="grid grid-cols-[28px_1fr_110px_110px] items-center gap-2 text-xs font-medium uppercase tracking-wide text-stone-400">
                <span>#</span><span>Medication</span><span>Dosage</span><span>Frequency</span>
              </div>
              {meds.entries.map((e, i) => (
                <div key={i} className="grid grid-cols-[28px_1fr_110px_110px] items-center gap-2">
                  <span className="text-xs text-stone-400">{i + 1}</span>
                  <Input value={e.name} onChange={(ev) => setEntry(i, "name", ev.target.value)} className="h-8 border-stone-300 bg-white text-sm" data-testid={`med-name-${i}`} />
                  <Input value={e.dosage} onChange={(ev) => setEntry(i, "dosage", ev.target.value)} className="h-8 border-stone-300 bg-white text-sm" data-testid={`med-dosage-${i}`} />
                  <Input value={e.frequency} onChange={(ev) => setEntry(i, "frequency", ev.target.value)} className="h-8 border-stone-300 bg-white text-sm" data-testid={`med-frequency-${i}`} />
                </div>
              ))}
            </div>
            <Textarea rows={3} placeholder="Additional medications or notes" value={meds.notes}
              onChange={(e) => setMeds({ ...meds, notes: e.target.value })}
              className="mt-4 border-stone-300 bg-white text-sm" data-testid="med-notes" />
            <div className="mt-4 flex justify-end">
              <Button onClick={saveMeds} disabled={savingMeds} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="med-save-button">
                {savingMeds ? "Saving..." : "Save Medication List"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
